import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

const FlowBuilderTemplateModal = ({ open, onSave, onUpdate, data, close }) => {
  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [connectionId, setConnectionId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [variables, setVariables] = useState({});

  const loadConnections = useCallback(async () => {
    try {
      const { data: list } = await api.get("/whatsapp");
      const arr = (Array.isArray(list) ? list : []).filter(
        (w) => w.channel === "pilotstatus"
      );
      setConnections(arr);
      if (arr.length && !connectionId) setConnectionId(String(arr[0].id));
    } catch (err) {
      // silencioso
    }
  }, [connectionId]);

  const loadTemplates = useCallback(async (connId) => {
    if (!connId) return;
    setLoading(true);
    try {
      const { data: list } = await api.get(`/pilot/${connId}/templates`);
      const arr = Array.isArray(list) ? list : list?.data || [];
      setTemplates(arr);
    } catch (err) {
      toast.error("Não foi possível carregar os modelos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open === "edit") {
      const d = data?.data || data || {};
      setTemplateName(d.templateName || "");
      setVariables(d.variables || {});
      setActiveModal(true);
      loadConnections();
    } else if (open === "create") {
      setTemplateName("");
      setVariables({});
      setActiveModal(true);
      loadConnections();
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (activeModal && connectionId) loadTemplates(connectionId);
  }, [activeModal, connectionId, loadTemplates]);

  const selected = templates.find((t) => t.name === templateName);

  useEffect(() => {
    if (!selected) return;
    const vars = selected.variables || [];
    setVariables((old) => {
      const next = {};
      vars.forEach((v) => {
        next[v] = old[v] !== undefined ? old[v] : "";
      });
      return next;
    });
  }, [templateName]); // eslint-disable-line

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = () => {
    if (!templateName) {
      toast.error("Selecione um modelo.");
      return;
    }
    const payload = { templateName, variables };
    if (open === "edit") {
      onUpdate({ ...data, data: { ...(data?.data || {}), ...payload } });
    } else {
      onSave(payload);
    }
    handleClose();
  };

  return (
    <Dialog open={activeModal} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {open === "create" ? "Adicionar Modelo (Template)" : "Editar Modelo (Template)"}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" style={{ color: "#6b7280", marginBottom: 12 }}>
          Envia um modelo aprovado pela Meta. É o único jeito de falar com o cliente
          quando a janela de 24h já fechou (ex: retomar conversa dias depois).
        </Typography>

        {connections.length > 1 && (
          <TextField
            select
            label="Conexão"
            value={connectionId}
            onChange={(e) => setConnectionId(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            style={{ marginBottom: 12 }}
            helperText="Só para listar aqui. No fluxo, usa a conexão do atendimento."
          >
            {connections.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {loading ? (
          <CircularProgress size={22} />
        ) : (
          <TextField
            select
            label="Modelo aprovado"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            style={{ marginBottom: 12 }}
          >
            {templates.length === 0 && (
              <MenuItem value="" disabled>
                Nenhum modelo encontrado
              </MenuItem>
            )}
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.name} disabled={t.sendable === false}>
                {t.name}{" "}
                {t.sendable === false ? `(${t.metaStatus || "não aprovado"})` : ""}
              </MenuItem>
            ))}
          </TextField>
        )}

        {selected && (
          <Box style={{ background: "#f8fafc", borderRadius: 8, padding: 12 }}>
            <Box style={{ marginBottom: 8 }}>
              <Chip
                size="small"
                label={selected.metaStatus || "-"}
                style={{
                  backgroundColor:
                    selected.sendable === false ? "#d97706" : "#16a34a",
                  color: "white",
                  marginRight: 6,
                }}
              />
              <Chip size="small" label={selected.category || "-"} />
            </Box>
            {(selected.variables || []).length > 0 ? (
              <>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 6 }}>
                  Preencha as variáveis do modelo. Pode usar variáveis do fluxo, ex:{" "}
                  {"{{resposta}}"}
                </Typography>
                {(selected.variables || []).map((v) => (
                  <TextField
                    key={v}
                    label={v}
                    value={variables[v] || ""}
                    onChange={(e) =>
                      { e.persist(); setVariables((old) => ({ ...old, [v]: e.target.value })); }
                    }
                    fullWidth
                    size="small"
                    variant="outlined"
                    style={{ marginBottom: 8 }}
                  />
                ))}
              </>
            ) : (
              <Typography variant="caption" color="textSecondary">
                Este modelo não tem variáveis.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {open === "create" ? "Adicionar" : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderTemplateModal;
